import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { EmptyState, ErrorState, StatusPill, type StatusTone } from '@bb/ui';
import { ApiError, type ImportSummary } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

export const IMPORT_STATUS: Record<ImportSummary['status'], [string, StatusTone]> = {
  uploaded: ['Mapping', 'neutral'],
  validating: ['Dry run…', 'info'],
  validated: ['Ready to apply', 'success'],
  validation_failed: ['No valid rows', 'danger'],
  applying: ['Applying…', 'info'],
  completed: ['Completed', 'success'],
  completed_with_errors: ['Completed with errors', 'warning'],
  failed: ['Failed', 'danger'],
  cancelled: ['Cancelled', 'neutral'],
};
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Audience → Import users (BB-36): the environment's CSV imports, newest first, and the upload that starts a new
 * one (mapping, dry run and apply follow on the import's page).
 */
export function ImportsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/audience/import' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/import' });
  const navigate = useNavigate();
  const permissions = usePermissions();
  const list = useQuery({ queryKey: ['imports', environmentId], queryFn: () => api.listImports(environmentId) });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadImport(environmentId, file, file.name),
    onSuccess: (created) => navigate({ to: '/env/$environmentId/audience/import/$importId', params: { environmentId, importId: created.id } }),
  });
  return (
    <section>
      <h1>Import users</h1>
      <p className="hint">
        A UTF-8 CSV with a header row and up to 100 MB: an <code>external_id</code> column (or <code>alias_label</code> and <code>alias_name</code>), attributes and subscription groups. Every import starts with a dry run; nothing changes until you apply it.
      </p>
      {permissions.has('profiles.import') && (
        <label className="button">
          Upload CSV
          <input type="file" accept=".csv,text/csv" className="visually-hidden" aria-label="CSV file" onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} disabled={upload.isPending} />
        </label>
      )}
      {upload.isPending && <p aria-busy="true">Uploading…</p>}
      {upload.isError && <p role="alert">{errorText(upload.error)}</p>}
      {list.isError ? (
        <ErrorState title="Imports could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <p aria-busy="true">Loading imports…</p>
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No imports yet" description="Upload a CSV to add or update users." />
      ) : (
        <table className="table" aria-label="Imports">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Uploader</th>
              <th scope="col">File name</th>
              <th scope="col">Records</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((i) => {
              const [label, tone] = IMPORT_STATUS[i.status];
              return (
                <tr key={i.id}>
                  <td>{formatWhen(i.createdAt)}</td>
                  <td>{i.uploadedBy?.email ?? '—'}</td>
                  <td>
                    <Link to="/env/$environmentId/audience/import/$importId" params={{ environmentId, importId: i.id }}>
                      {i.fileName}
                    </Link>
                  </td>
                  <td>{i.totalRows === null ? '—' : i.rowsApplied ? `${i.rowsApplied.toLocaleString('en-US')} of ${i.totalRows.toLocaleString('en-US')} applied` : i.totalRows.toLocaleString('en-US')}</td>
                  <td>
                    <StatusPill tone={tone}>{label}</StatusPill>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
